import { Box, SxProps, Theme, useTheme } from '@mui/material'
import {
  DataGridPremium,
  FooterPropsOverrides,
  GridAggregationFunction,
  GridAggregationModel,
  GridApiPremium,
  GridCellParams,
  GridColDef,
  GridColumnDimensions,
  GridColumnOrderChangeParams,
  GridColumnVisibilityModel,
  GridDensity,
  gridExpandedSortedRowEntriesSelector,
  GridFilterModel,
  gridPaginatedVisibleSortedGridRowEntriesSelector,
  GridPinnedColumnFields,
  GridRenderCellParams,
  GridRowClassNameParams,
  GridRowId,
  GridRowSelectionModel,
  GridSortModel,
  GridToolbarProps,
  GridTreeNodeWithRender,
  GridValueFormatter,
  ToolbarPropsOverrides,
} from '@mui/x-data-grid-premium'

declare module '@mui/x-data-grid-premium' {
  interface LoadingOverlayPropsOverrides {
    overlay?: boolean
    message?: string
  }
}

import { useEntitiesFromSearch } from 'api/search'
import { ncoStatusFilters } from 'components/DataGrid/filterArrays'
import { filterPanelProps } from 'components/MTB/grid/style'
import { useLayoutContext } from 'context/LayoutProvider/LayoutProvider'
import React, { Dispatch, FC, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react'
import { GROBxLoader } from 'theme/components'
import {
  DrawerBaseProps,
  DrawerWrapperInjectedProps,
  Lead,
  NewDeal,
  OrganizationTableItem,
  OrgTreeRow,
  Task,
  Timeline,
  TreeRow,
} from 'types'
import { initialVisibilityState } from '../../components/Deals/grid/defaultModels'
import CustomNoResultsOverlay from './CustomNoResultsOverlay'
import { CustomNoRowsOverlay } from './CustomNoRowsOverlay'
import './DataGridContext.css'
import FooterOutlet from './Footer/FooterOutlet'
import { loadModel, saveModel } from './operators/loadModels'

const currencyValueFormatter: GridValueFormatter = ({ value }) =>
  value == null
    ? ''
    : (value as number).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })

export const safeSum: GridAggregationFunction<number, number> = {
  label: 'Safe sum',
  apply: ({ values }) => values.reduce((sum, v) => (typeof v === 'number' && !Number.isNaN(v) ? sum + v : sum), 0),
  valueFormatter: currencyValueFormatter, // now matches: GridValueFormatter<GridValidRowModel, any, ...>
}
interface GlobalDataGridProps {
  columns: GridColDef[]
  // rows: OrgTreeRow[]
  rows: OrgTreeRow[] | OrganizationTableItem[] | TreeRow[] | Task[] | Timeline[]
  isExpanded?: (id: GridRowId) => boolean | false
  apiRef: React.RefObject<GridApiPremium>
  context: string
  defaultFilterModel: GridFilterModel
  defaultVisibilityModel: GridColumnVisibilityModel
  defaultSortModel: GridSortModel
  defaultGridDensity: 'compact' | 'standard' | 'comfortable'
  defaultColumnWidths?: GridColumnDimensions
  defaultColumnOrder?: string[]
  pinnedColumns?: GridPinnedColumnFields
  aggregationModel?: GridAggregationModel
  onRowSelectionModelChange?: (newModel: GridRowSelectionModel) => void
  onChangeColumnVisibility?: (newModel: GridColumnVisibilityModel) => void
  onChangeMonth?: (month: string) => void
  monthFilter?: string
  toolbarComponent?: React.JSXElementConstructor<GridToolbarProps & ToolbarPropsOverrides>
  onRowClick?: (row: OrganizationTableItem | Task) => void
  onChangeDensity?: (newDensity: GridDensity) => void
  onClearFilters?: () => void
  onChangeView?: (
    newView: string,
    setFilterModel: Dispatch<SetStateAction<GridFilterModel>>,
    setColumnVisibilityModel: Dispatch<SetStateAction<GridColumnVisibilityModel>>
  ) => void
  selectedView?: string
  setSelectedView?: Dispatch<SetStateAction<string>>
  customView?: string
  setCustomView?: Dispatch<SetStateAction<string>>
  FooterComponent?: React.FC<any>
  footerProps?: any
  onProcessRowUpdate?: (
    newRow: OrganizationTableItem | Task | NewDeal | Lead,
    oldRow: OrganizationTableItem | Task | NewDeal | Lead,
    rowSelectionModel: GridRowSelectionModel
  ) => void | Promise<any>
  getRowClassName?: (params: GridRowClassNameParams, rowSelectionModel: GridRowSelectionModel) => string
  getCellClassName?: (params: GridCellParams, rowSelectionModel: GridRowSelectionModel) => string
  isCellEditable?: (
    params: GridRenderCellParams<any, any, any, GridTreeNodeWithRender>,
    rowSelectionModel: GridRowSelectionModel
  ) => boolean
  isRowSelectable?: (params: { row: any }) => boolean
  treeData: boolean
  gridSxProps?: SxProps<Theme>
  isLoading?: boolean
  isPinnedRow?: (row: any) => boolean // optional predicate per page
  pinLocation?: 'top' | 'bottom'
  disableChildFilters?: boolean
  checkboxSelection?: boolean
  DrawerComponent?: React.ComponentType<{
    visibleRowIds?: string[]
    Component: React.ComponentType<DrawerBaseProps & DrawerWrapperInjectedProps>
    drawerProps: DrawerBaseProps
  }>

  drawerProps?: DrawerBaseProps & {
    Component: React.ComponentType<DrawerBaseProps & DrawerWrapperInjectedProps>
  }
  isDrawerOpen?: boolean
  selectedRowId?: string | null
  lastSelectedRowId?: string | null
  onClearLastSelectedRow?: () => void
  gridContainerSx?: SxProps<Theme>
  // Server-side mode props
  serverSideMode?: boolean
  rowCount?: number
  paginationModel?: { page: number; pageSize: number }
  onPaginationModelChange?: (model: { page: number; pageSize: number }) => void
  onSortModelChange?: (model: GridSortModel) => void
  onFilterModelChange?: (model: GridFilterModel) => void
  // Sync status for server-side mode indicator
  syncStatus?: {
    isInitialized?: boolean
    documentCount?: number
    lastSyncTime?: string
    isLoading?: boolean
  }
  // Server-provided aggregations (used instead of client-side when in server-side mode)
  serverAggregations?: {
    total?: number
    monthlyTotal?: number
    spend?: number
    grossProfit?: number
    totalGrossProfit?: number
    implementationFee?: number
  }
  /** Allow inline editing when multiple rows are selected (for mass updates) */
  allowInlineMassEdit?: boolean
}

const GlobalDataGrid: FC<GlobalDataGridProps> = ({
  columns,
  rows,
  isExpanded,
  apiRef,
  context,
  defaultFilterModel,
  defaultVisibilityModel,
  defaultSortModel,
  defaultGridDensity,
  defaultColumnWidths,
  defaultColumnOrder,
  pinnedColumns,
  aggregationModel,
  onRowSelectionModelChange,
  onChangeColumnVisibility,
  onChangeDensity,
  onChangeMonth,
  monthFilter,
  toolbarComponent,
  FooterComponent,
  footerProps,
  onClearFilters,
  onChangeView,
  selectedView,
  setSelectedView,
  customView,
  setCustomView,
  onProcessRowUpdate,
  getRowClassName,
  getCellClassName,
  isCellEditable,
  isRowSelectable,
  treeData,
  gridSxProps,
  isLoading,
  isPinnedRow,
  pinLocation,
  disableChildFilters,
  checkboxSelection,
  DrawerComponent,
  drawerProps,
  isDrawerOpen,
  selectedRowId,
  lastSelectedRowId,
  onClearLastSelectedRow,
  gridContainerSx,
  // Server-side mode props
  serverSideMode,
  rowCount,
  paginationModel,
  onPaginationModelChange,
  onSortModelChange,
  onFilterModelChange,
  syncStatus,
  serverAggregations,
  allowInlineMassEdit,
}) => {
  //////////////////////////// Theme & Layout ///////////////////////////////////
  const theme = useTheme()
  const { pinned } = useLayoutContext()
  // //////////////////////// DataGrid State /////////////////////////////////
  const { isItemsLoading } = useEntitiesFromSearch()
  const [filterModel, setFilterModel] = useState<GridFilterModel>(() =>
    loadModel<GridFilterModel>(context, 'FilterModel', {
      ...defaultFilterModel,
      quickFilterExcludeHiddenColumns: false,
    })
  )
  const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>(() =>
    loadModel<GridColumnVisibilityModel>(context, 'ColumnVisibilityModel', defaultVisibilityModel)
  )
  const [sortModel, setSortModel] = useState<GridSortModel>(() =>
    loadModel<GridSortModel>(context, 'SortModel', defaultSortModel)
  )
  const [densityModel, setDensityModel] = useState<GridDensity>(() =>
    loadModel<GridDensity>(context, 'DensityModel', defaultGridDensity)
  )
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>({
    ids: new Set<string>(),
    type: 'include',
  })
  const [columnWidths, setColumnWidths] = useState<GridColumnDimensions>(() =>
    loadModel<GridColumnDimensions>(context, 'ColumnWidths', defaultColumnWidths)
  )
  const [columnOrder, setColumnOrder] = useState<string[]>(() =>
    loadModel<string[]>(context, 'ColumnOrder', defaultColumnOrder ?? [])
  )
  const [pinnedColumnsModel, setPinnedColumnsModel] = useState<GridPinnedColumnFields>(() => {
    const saved = loadModel<GridPinnedColumnFields>(context, 'PinnedColumns', null)
    // Merge saved pinned columns with default, ensuring defaults are always included
    if (saved) {
      return {
        left: [...new Set([...(pinnedColumns?.left ?? []), ...(saved.left ?? [])])],
        right: [...new Set([...(pinnedColumns?.right ?? []), ...(saved.right ?? [])])],
      }
    }
    return pinnedColumns ?? { left: [], right: [] }
  })
  const [selectedRowCount, setSelectedRowCount] = useState<number>(0)
  const [visibleRowIds, setVisibleRowIds] = React.useState<string[]>([])
  const [_anchorEl, setAnchorEl] = useState<Element | null>(null)

  const pickEntries = React.useMemo(
    () => (treeData ? gridExpandedSortedRowEntriesSelector : gridPaginatedVisibleSortedGridRowEntriesSelector),
    [treeData]
  )

  // helper: shallow-equal arrays
  const arraysEqual = (a: string[], b: string[]) => a.length === b.length && a.every((v, i) => v === b[i])
  const isMountedRef = React.useRef(false)

  React.useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
    }
  }, [])
  const handleStateChange = React.useCallback(
    (state: any) => {
      if (!isMountedRef.current) return

      const entries = pickEntries(state)
      const nextIds = entries.map((e: any) => String(e.id))

      // Defer to avoid setState during DataGrid's render phase
      queueMicrotask(() => {
        if (!isMountedRef.current) return
        setVisibleRowIds(prev => (arraysEqual(prev, nextIds) ? prev : nextIds))
      })
    },
    [pickEntries]
  )
  // const handleStateChange = React.useCallback(
  //   (state: any) => {
  //     const entries = pickEntries(state)
  //     const nextIds = entries.map((e: any) => String(e.id))

  //     // only set if changed
  //     setVisibleRowIds(prev => (arraysEqual(prev, nextIds) ? prev : nextIds))
  //   },
  //   [pickEntries]
  // )

  // if you need full models:
  // const visibleRows = React.useMemo(() => visibleEntries.map(e => e.model as GridValidRowModel), [visibleEntries])

  // //////////////////////////////////////////////////////////////////////////////
  ////////////////////// DataGrid handle functions ///////////////////////////////
  // DataGrid may call these handlers during its own render phase (e.g. when
  // normalising controlled props).  Wrapping the setState calls in
  // queueMicrotask defers them to after render, avoiding the React warning:
  // "Cannot update a component while rendering a different component".
  const changeColumnVisibility = useCallback(
    (newModel: GridColumnVisibilityModel) => {
      queueMicrotask(() => {
        if (!isMountedRef.current) return
        setColumnVisibilityModel(newModel)
        saveModel(context, 'ColumnVisibilityModel', newModel)
        onChangeColumnVisibility?.(newModel)
      })
    },
    [context, onChangeColumnVisibility]
  )
  const changeFilterModel = useCallback(
    (newModel: GridFilterModel) => {
      queueMicrotask(() => {
        if (!isMountedRef.current) return
        setFilterModel(newModel)
        saveModel(context, 'FilterModel', newModel)
        onFilterModelChange?.(newModel)
      })
    },
    [context, onFilterModelChange]
  )
  const changeSortModel = useCallback(
    (newModel: GridSortModel) => {
      queueMicrotask(() => {
        if (!isMountedRef.current) return
        setSortModel(newModel)
        saveModel(context, 'SortModel', newModel)
        onSortModelChange?.(newModel)
      })
    },
    [context, onSortModelChange]
  )
  const changeDensityModel = useCallback(
    (newModel: GridDensity) => {
      queueMicrotask(() => {
        if (!isMountedRef.current) return
        setDensityModel(newModel)
        saveModel(context, 'DensityModel', newModel)
      })
    },
    [context]
  )

  // Wrapped setFilterModel that also notifies server-side mode
  // NOTE: onFilterModelChange must be called OUTSIDE the setState updater
  // to avoid triggering parent state updates during render phase
  const wrappedSetFilterModel: typeof setFilterModel = useCallback(
    valueOrUpdater => {
      if (!isMountedRef.current) return
      setFilterModel(prev => {
        const newModel = typeof valueOrUpdater === 'function' ? valueOrUpdater(prev) : valueOrUpdater
        // Defer parent notification to after render
        queueMicrotask(() => {
          if (!isMountedRef.current) return
          onFilterModelChange?.(newModel)
        })
        return newModel
      })
    },
    [onFilterModelChange]
  )

  const changeRowSelectionModel = useCallback(
    (newModel: GridRowSelectionModel) => {
      queueMicrotask(() => {
        if (!isMountedRef.current) return
        setRowSelectionModel(newModel)
        setSelectedRowCount(newModel?.ids?.size)
        onRowSelectionModelChange?.(newModel)
      })
    },
    [onRowSelectionModelChange]
  )
  const clearVisibility = () => {
    // apiRef.current.setColumnVisibilityModel(defaultVisibilityModel)
    setColumnVisibilityModel(defaultVisibilityModel)
    saveModel(context, 'ColumnVisibilityModel', defaultVisibilityModel)
  }
  const clearSorting = () => {
    // apiRef.current.setSortModel(defaultSortModel)
    setSortModel(defaultSortModel)
    saveModel(context, 'SortModel', defaultSortModel)
  }
  const _resizeColumns = () => {
    if (apiRef.current) {
      apiRef.current.updateColumns(columns)
    }
  }
  const changeColumnWidth = ({ colDef, width }) => {
    const newWidths = {
      ...columnWidths,
      [colDef.field]: width,
    }
    setColumnWidths(newWidths)
    saveModel(context, 'ColumnWidths', newWidths)
  }
  const resetColumnWidths = () => {
    setColumnWidths(defaultColumnWidths)
    saveModel(context, 'ColumnWidths', defaultColumnWidths)
  }
  const changeColumnOrder = (params: GridColumnOrderChangeParams) => {
    // Get the current column order from the API
    const allColumns = apiRef.current.getAllColumns()
    const newOrder = allColumns.map(col => col.field)
    setColumnOrder(newOrder)
    saveModel(context, 'ColumnOrder', newOrder)
  }
  const resetColumnOrder = () => {
    setColumnOrder(defaultColumnOrder ?? [])
    saveModel(context, 'ColumnOrder', defaultColumnOrder ?? [])
  }
  const changePinnedColumns = (newPinnedColumns: GridPinnedColumnFields) => {
    setPinnedColumnsModel(newPinnedColumns)
    saveModel(context, 'PinnedColumns', newPinnedColumns)
  }
  const resetPinnedColumns = () => {
    setPinnedColumnsModel(pinnedColumns ?? { left: [], right: [] })
    saveModel(context, 'PinnedColumns', null)
  }

  const deleteClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(e.currentTarget)
  }
  //////////////////////////////////////////////////////////////////////////////
  /////////////////////////////// Pinned Rows ///////////////////////////////////
  const MAX_PINNED = 10
  const getId = (r: any) => r.rowId
  const pinTo = pinLocation ?? 'top'

  // Gather all rows that match the page-level isPinnedRow predicate
  const allPinnedRows: any[] = React.useMemo(() => {
    if (!isPinnedRow) return []
    return (rows as any[]).filter(r => isPinnedRow(r))
  }, [rows, isPinnedRow])

  // Safe extractor: supports Date, number (epoch), ISO string; null/invalid => Infinity (i.e., lowest priority)
  const getCompletedAt = (r: any): number => {
    const v = r?.timeCompleted
    if (!v) return Number.POSITIVE_INFINITY
    if (v instanceof Date) return v.getTime()
    if (typeof v === 'number') return v
    const t = Date.parse(v) // handles ISO strings
    return Number.isFinite(t) ? t : Number.POSITIVE_INFINITY
  }

  // 2) Sort by oldest first (smallest timestamp first) so “most recent goes to the bottom of the 5”
  const sortedPinnedByAge = React.useMemo(() => {
    return [...allPinnedRows].sort((a, b) => {
      const ta = getCompletedAt(a)
      const tb = getCompletedAt(b)
      if (ta !== tb) return ta - tb // earlier (older) first
      // optional tiebreaker: stable by rowId
      return String(getId(a)).localeCompare(String(getId(b)))
    })
  }, [allPinnedRows])

  // Pick up to MAX_PINNED to actually pin
  const pinnedTopRows: any[] = React.useMemo(() => {
    if (pinTo !== 'top') return []
    return sortedPinnedByAge.slice(0, MAX_PINNED)
  }, [sortedPinnedByAge, pinTo])

  const pinnedBottomRows: any[] = React.useMemo(() => {
    if (pinTo !== 'bottom') return []
    return sortedPinnedByAge.slice(0, MAX_PINNED)
  }, [sortedPinnedByAge, pinTo])

  // IDs of the actively pinned (for height/classnames)
  const activelyPinnedIdSet = React.useMemo(() => new Set([...pinnedTopRows, ...pinnedBottomRows].map(getId)), [
    pinnedTopRows,
    pinnedBottomRows,
  ])

  // Optional: overflow set (not strictly required, but handy for classNames)
  const overflowPinnedIdSet = React.useMemo(() => {
    const active = activelyPinnedIdSet
    return new Set(allPinnedRows.map(getId).filter(id => !active.has(id)))
  }, [allPinnedRows, activelyPinnedIdSet])

  // Row height: only compress the actively pinned ones
  const baseRowHeight = densityModel === 'compact' ? 32 : densityModel === 'comfortable' ? 60 : 52

  const getRowHeight = React.useCallback((params: any) => (activelyPinnedIdSet.has(params.id) ? 28 : baseRowHeight), [
    activelyPinnedIdSet,
    baseRowHeight,
  ])

  // For classNames: tag pinned & overflow-pinned (optional but nice)
  const cx = (...parts: (string | undefined | null | false)[]) =>
    Array.from(new Set(parts.filter(Boolean).join(' ').trim().split(/\s+/))).join(' ')

  // const composedGetRowClassName = React.useCallback(
  //   (params: GridRowClassNameParams<RowItem>) => {
  //     const fromProps = getRowClassName ? getRowClassName(params, rowSelectionModel) : ''
  //     const isActivelyPinned = activelyPinnedIdSet.has(params.id as string)
  //     const isOverflowPinned = overflowPinnedIdSet.has(params.id as string)
  //     return cx(fromProps, isActivelyPinned && 'pinned-row', isOverflowPinned && 'pinned-row-overflow')
  //   },
  //   [getRowClassName, rowSelectionModel, activelyPinnedIdSet, overflowPinnedIdSet]
  // )

  // const composedGetCellClassName = React.useCallback(
  //   (params: GridCellParams<RowItem, any, any, GridTreeNodeWithRender>) => {
  //     const fromProps = getCellClassName ? getCellClassName(params, rowSelectionModel) : ''
  //     return cx(fromProps)
  //   },
  //   [getCellClassName, rowSelectionModel]
  // )
  const composedGetRowClassName = React.useCallback(
    (params: any) => {
      const fromProps = getRowClassName ? getRowClassName(params, rowSelectionModel) : ''

      const isDrawerRow = !!selectedRowId && params.id === selectedRowId
      const isLastSelected = !!lastSelectedRowId && params.id === lastSelectedRowId

      return cx(
        fromProps,
        isDrawerRow && 'drawer-current-row',
        !isDrawerRow && isLastSelected && 'temp-selected-row' // don’t double-class the drawer row
      )
    },
    [getRowClassName, rowSelectionModel, selectedRowId, lastSelectedRowId]
  )

  const composedGetCellClassName = React.useCallback(
    params => {
      const fromProps = getCellClassName ? getCellClassName(params, rowSelectionModel) : ''
      const isViewedCell = selectedRowId && params.id === selectedRowId && params.field === 'name'
      return cx(fromProps, isViewedCell && 'drawer-current-cell')
    },
    [getCellClassName, rowSelectionModel, selectedRowId]
  )
  // Build the server aggregations pinned bottom row when in server-side mode
  const serverAggregationsRow = React.useMemo(() => {
    if (!serverSideMode || !serverAggregations) return null
    // Create a synthetic row with aggregation values
    // Use rowId to match the getId function: (r) => r.rowId
    return {
      rowId: '__SERVER_AGGREGATIONS_ROW__',
      isAggregationRow: true,
      total: serverAggregations.total ?? 0,
      monthlyTotal: serverAggregations.monthlyTotal ?? 0,
      spend: serverAggregations.spend ?? 0,
      grossProfit: serverAggregations.grossProfit ?? 0,
      totalGrossProfit: serverAggregations.totalGrossProfit ?? 0,
      implementationFee: serverAggregations.implementationFee ?? 0,
      // Add a name field for display
      name: 'Total (All Filtered)',
    }
  }, [serverSideMode, serverAggregations])

  // Build the pinnedRows prop (at most 5 shown)
  const pinnedRowsProp = React.useMemo(() => {
    const bottomRows = [...pinnedBottomRows]

    // Add server aggregations row at the bottom if available
    if (serverAggregationsRow) {
      bottomRows.push(serverAggregationsRow)
    }

    if (pinnedTopRows.length || bottomRows.length) {
      return { top: pinnedTopRows, bottom: bottomRows }
    }
    return undefined
  }, [pinnedTopRows, pinnedBottomRows, serverAggregationsRow])

  //////////////////////////////////////////////////////////////////////////////
  ///////////////////////////// Initial State ///////////////////////////////////
  // When using server-side aggregations, disable client-side aggregation model
  // to prevent the grid from calculating sums on only visible rows
  const effectiveAggregationModel = serverSideMode && serverAggregations ? {} : aggregationModel ?? {}

  const initialState = {
    sorting: { sortModel },
    filter: {
      filterModel: {
        ...filterModel,
        quickFilterExcludeHiddenColumns: false,
      },
    },
    columns: defaultVisibilityModel ?? {},
    aggregation: {
      model: effectiveAggregationModel,
    },
  }
  const adjustedColumns = useMemo(() => {
    // Apply widths: if the user manually resized a column, lock to that width.
    // Otherwise add flex so columns stretch to fill available space on wider
    // screens, using the column's defined width as the minWidth floor.
    const columnsWithWidths = columns.map(col => {
      const savedWidth = columnWidths?.[col.field]
      if (savedWidth != null) {
        // User has manually resized — lock to that width, drop flex
        const { flex: _flex, minWidth: _mw, ...rest } = col
        return { ...rest, width: savedWidth }
      }
      // No manual resize — use flex to fill space, minWidth keeps it readable
      const baseWidth = col.width ?? 100
      return {
        ...col,
        flex: col.flex ?? 1,
        minWidth: col.minWidth ?? baseWidth,
        width: undefined, // let flex control sizing
      }
    })

    // If we have a saved column order, reorder the columns accordingly
    if (columnOrder && columnOrder.length > 0) {
      const columnMap = new Map(columnsWithWidths.map(col => [col.field, col]))
      const orderedColumns: GridColDef[] = []

      // Add columns in the saved order
      for (const field of columnOrder) {
        const col = columnMap.get(field)
        if (col) {
          orderedColumns.push(col)
          columnMap.delete(field)
        }
      }

      // Add any remaining columns that weren't in the saved order (new columns)
      for (const col of columnMap.values()) {
        orderedColumns.push(col)
      }

      return orderedColumns
    }

    return columnsWithWidths
  }, [columns, columnWidths, columnOrder])
  //////////////////////////////////////////////////////////////////////////////
  // Only notify parent of density changes AFTER initial mount
  const densityMountedRef = React.useRef(false)
  useEffect(() => {
    if (!densityMountedRef.current) {
      densityMountedRef.current = true
      return // Skip the initial mount — parent already knows the default
    }
    if (onChangeDensity) onChangeDensity(densityModel)
  }, [densityModel, onChangeDensity])

  const defaultSxProps = {
    '--disabled-row-background': theme.palette.background.defaultdim,
    '--parent-org-background': theme.palette.background.defaultdim,
    '--selected-row-background': theme.palette.background.help,
    '--temp-selected-row': theme.palette.background.help,
    '--row-hover-background': theme.palette.text.dirty,
    '--nco-expand-color': isDrawerOpen ? theme.palette.text.disabled : theme.palette.text.primary,
    '--nco-expand-events': isDrawerOpen ? 'none' : 'auto',
    '--postdated-background': theme.palette.background.postdated,
  }
  const resolvedGridSx = typeof gridSxProps === 'function' ? gridSxProps(theme) : gridSxProps
  const footerSlotProps = React.useMemo(
    () => ({
      // Use the universal component the page passes in (FooterOutlet expects TaskFooter)
      TaskFooter: FooterComponent,
      // Footer's own props (FooterOutlet expects taskFooterProps)
      taskFooterProps: footerProps,
      // Give the outlet access to selection state and its setter:
      rowSelectionModel,
      setRowSelectionModel,
      // Props for the global (non-selection) footer
      globalFooterProps: {
        filterModel,
        setFilterModel,
        selectedRowCount,
        rowSelectionModel,
        setRowSelectionModel,
        serverSideMode,
        onClearFilters,
        onFilterModelChange,
        monthFilter,
        onChangeMonth,
      },
    }),
    [
      FooterComponent,
      footerProps,
      rowSelectionModel,
      setRowSelectionModel,
      filterModel,
      setFilterModel,
      selectedRowCount,
      serverSideMode,
      onClearFilters,
      onFilterModelChange,
    ]
  )
  return (
    <Box
      onClickCapture={e => {
        const target = e.target as HTMLElement
        const clickedRow = target.closest('.MuiDataGrid-row')
        const clickedCell = target.closest('.MuiDataGrid-cell')
        const clickedPanel = target.closest('.MuiDataGrid-panel')
        const clickedToolbar = target.closest('.MuiDataGrid-toolbar')
        const clickedFooter = target.closest('.MuiDataGrid-footerContainer')
        const clickedViewMenu = target.closest('.task-view-menu')
        const clickedBackdrop = target.closest('.MuiBackdrop-root')
        const clickedFilterContainer = target.closest('custom-filters-container')

        // If click wasn't a row/cell/panel/toolbar, treat as background click
        if (
          !clickedRow &&
          !clickedCell &&
          !clickedPanel &&
          !clickedToolbar &&
          !clickedFooter &&
          !clickedViewMenu &&
          !clickedBackdrop &&
          !clickedFilterContainer &&
          !isDrawerOpen
        ) {
          onClearLastSelectedRow?.()
        }
      }}
      sx={
        gridContainerSx
          ? gridContainerSx
          : {
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
              maxHeight: '100vh',
              width: '100%',
              paddingTop: pinned ? '60px' : 1.5,
              transition: 'all 0.3s ease',
              height: '100vh',
              overflow: 'visible',
            }
      }
    >
      <DataGridPremium
        className={`global-data-grid ${isDrawerOpen ? 'has-drawer-open' : ''}`}
        initialState={initialState}
        rows={rows}
        pinnedRows={pinnedRowsProp}
        getRowId={getId}
        columns={adjustedColumns}
        treeData={treeData}
        {...(treeData && { getTreeDataPath: row => row.treePath })}
        showToolbar
        isGroupExpandedByDefault={node => isExpanded?.(node.id) ?? false}
        groupingColDef={{
          headerName: '',
          width: 0,
          renderCell: () => null,
          headerClassName: `${context}-grouping-header`,
        }}
        pinnedColumns={pinnedColumnsModel}
        onPinnedColumnsChange={changePinnedColumns}
        apiRef={apiRef}
        rowHeight={baseRowHeight}
        getRowHeight={getRowHeight}
        checkboxSelection={checkboxSelection ?? true}
        disableRowSelectionOnClick
        rowSelectionModel={rowSelectionModel}
        filterModel={filterModel}
        columnVisibilityModel={columnVisibilityModel}
        sortModel={sortModel}
        onStateChange={handleStateChange}
        onRowSelectionModelChange={changeRowSelectionModel}
        onFilterModelChange={changeFilterModel}
        onColumnVisibilityModelChange={changeColumnVisibility}
        onColumnHeaderClick={() => onClearLastSelectedRow?.()}
        onSortModelChange={changeSortModel}
        density={densityModel}
        onDensityChange={changeDensityModel}
        {...(isCellEditable && {
          isCellEditable: (params: GridRenderCellParams<any, any, any, GridTreeNodeWithRender>) =>
            isCellEditable(params, rowSelectionModel),
        })}
        isRowSelectable={isRowSelectable || (params => !params?.row?.isProduct)}
        rowBufferPx={300}
        columnBufferPx={0}
        scrollbarSize={12}
        // Server-side mode props
        {...(serverSideMode && {
          paginationMode: 'server',
          sortingMode: 'server',
          filterMode: 'server',
          rowCount: rowCount ?? 0,
          paginationModel: paginationModel,
          onPaginationModelChange: onPaginationModelChange,
          pageSizeOptions: [25, 50, 100, 250, { value: -1, label: 'All' }],
          pagination: true,
        })}
        // Control aggregation model as prop (not just initialState) to properly disable when using server aggregations
        aggregationModel={effectiveAggregationModel}
        onColumnWidthChange={changeColumnWidth}
        onColumnOrderChange={changeColumnOrder}
        rowSelectionPropagation={{
          descendants: false,
          parents: false,
        }}
        disableChildrenFiltering={disableChildFilters ?? false}
        onCellEditStart={(params, event) => {
          // Allow editing when multiple rows selected if inline mass edit is enabled
          if (rowSelectionModel.ids.size > 1 && !allowInlineMassEdit) {
            event.defaultMuiPrevented = true // Prevent editing
          }
        }}
        processRowUpdate={(newRow, oldRow, context) =>
          onProcessRowUpdate ? onProcessRowUpdate(newRow, oldRow, rowSelectionModel) : newRow
        }
        onProcessRowUpdateError={error => console.error('Row Update Error:', error)}
        slots={{
          toolbar: toolbarComponent,
          footer: FooterOutlet,
          loadingOverlay: GROBxLoader,
          noRowsOverlay: CustomNoRowsOverlay,
          noResultsOverlay: CustomNoResultsOverlay,
        }}
        getRowClassName={composedGetRowClassName}
        getCellClassName={composedGetCellClassName}
        slotProps={{
          // Only pass custom toolbar props when a custom toolbar component is provided
          // MUI's default GridToolbar spreads props to DOM elements, causing React warnings
          // Cast to any to support custom toolbar props not in MUI's type definitions
          //           loadingOverlay: {
          // open: isLoading,
          // customLetters:
          //           },
          loadingOverlay: {
            overlay: true,
            message: `Syncing ${context.charAt(0).toUpperCase() + context.slice(1)}`,
          },
          toolbar: (toolbarComponent
            ? {
                filterModel,
                setFilterModel: serverSideMode ? wrappedSetFilterModel : setFilterModel,
                setColumnVisibilityModel,
                selectionModel: rowSelectionModel,
                initialVisibilityState,
                defaultSortModel,
                sortModel,
                columnWidths: defaultColumnWidths,
                onClearSorting: clearSorting,
                onClearVisibility: clearVisibility,
                onChangeVisibility: changeColumnVisibility,
                onClearWidths: resetColumnWidths,
                columnOrder: columnOrder,
                defaultColumnOrder: defaultColumnOrder,
                onClearColumnOrder: resetColumnOrder,
                pinnedColumnsModel: pinnedColumnsModel,
                onClearPinnedColumns: resetPinnedColumns,
                onChangeDensity: onChangeDensity,
                onChangeView: onChangeView,
                selectedView: selectedView,
                setSelectedView: setSelectedView,
                customView: customView,
                setCustomView: setCustomView,
                statusLabel: 'Onboarding Status',
                filterOptions: ncoStatusFilters,
                context,
                boardTheme: `icon.${context}`,
                // Server-side sync status
                syncStatus: serverSideMode ? syncStatus : undefined,
                totalRowCount: serverSideMode ? rowCount : undefined,
                onChangeMonth: onChangeMonth,
                monthFilter: monthFilter,
                onClearFilters,
              }
            : {}) as any,
          footer: footerSlotProps as FooterPropsOverrides,
          panel: {
            style: {
              border: '1px solid var(--mui-palette-background-defaultdim)',
              borderRadius: '4px',
            },
          } as any,
          filterPanel: {
            sx: {
              ...filterPanelProps,
            },
          },
        }}
        sx={{
          flex: 1,
          minHeight: 0,
          width: '100%',

          ...defaultSxProps,
          ...(resolvedGridSx ?? {}),
        }}
        loading={isLoading || isItemsLoading}
      />
      {isDrawerOpen && DrawerComponent ? (
        <DrawerComponent visibleRowIds={visibleRowIds} Component={drawerProps.Component} drawerProps={drawerProps} />
      ) : null}
    </Box>
  )
}
export default GlobalDataGrid
